// Self-contained multi-series SVG line chart (no chart dependency). Built for the
// Costing view: one line per resource/service over a daily date axis, with a
// hover crosshair + tooltip. Pure presentational — give it { dates, series }.

import { useEffect, useRef, useState } from 'react'
import { Box, Paper, Typography, useTheme } from '@mui/material'

const PALETTE = [
  '#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#d32f2f',
  '#0288d1', '#7b1fa2', '#558b2f', '#f57c00', '#c2185b',
]

function niceMax(v) {
  if (v <= 0) return 1
  const pow = Math.pow(10, Math.floor(Math.log10(v)))
  const n = v / pow
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
  return step * pow
}

// Splits a series' points into contiguous runs of present-value date
// indices, breaking wherever a date's point is entirely absent (undefined)
// -- an explicitly-present 0 stays in its run (a real, if unusual, value);
// only a missing key is treated as "no data" and breaks the line. Each run
// becomes its own <polyline>, so a chart whose series can have gaps (e.g.
// Contacts per hour, where an agent had no covered occurrences that
// day/week/month) draws a break instead of a false dip toward zero. Every
// other current caller zero-fills every date (fillSeries), so this never
// changes their rendering -- there's simply never a gap for them to hit.
function presentRuns(dates, points) {
  const runs = []
  let current = []
  for (let i = 0; i < dates.length; i++) {
    if (points[dates[i]] === undefined) {
      if (current.length) runs.push(current)
      current = []
    } else {
      current.push(i)
    }
  }
  if (current.length) runs.push(current)
  return runs
}

// dates: ['YYYY-MM-DD', ...] sorted. series: [{ key, label, points: {date: cost} }].
// A point missing from `points` (key absent, not present-as-0) renders as a
// gap in that series' line rather than a false zero -- see presentRuns().
// valueFmt formats a numeric cost for axis/tooltip. height/width are the SVG box.
// showValueLabels: opt-in (default off, so every existing caller is
// unaffected) -- prints valueFmt(value) directly above each point, not just
// on hover. Best suited to a chart with one series and a modest point count
// (e.g. week-over-week, not a dense daily series with many agents at once),
// since labels aren't collision-avoided against each other. Also means the
// values are baked into the actual <svg>, so they survive a PNG export
// (exportChartPng.js) where hover obviously can't.
// responsive: opt-in (default off) -- draws at the container's real pixel
// width instead of stretching an 820-wide drawing to fit, which pulls the
// text wide on a large screen. Off by default because a caller that exports
// the <svg> (exportChartPng.js) may rely on the fixed 820 viewBox.
// logScale: opt-in -- plots log10(1 + v), so one large day does not flatten
// every other series against the axis. Ticks stay in real values.
export default function LineChart({ dates, series, valueFmt = (v) => v.toFixed(2), height = 320, showValueLabels = false, responsive = false, logScale = false }) {
  const theme = useTheme()
  const gridColor = theme.palette.divider
  const labelColor = theme.palette.text.secondary
  const [hover, setHover] = useState(null) // { i, x }
  const boxRef = useRef(null)
  const [boxWidth, setBoxWidth] = useState(null)
  useEffect(() => {
    if (!responsive || !boxRef.current || typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(([entry]) => setBoxWidth(Math.round(entry.contentRect.width)))
    ro.observe(boxRef.current)
    return () => ro.disconnect()
  }, [responsive, dates?.length > 0 && series?.length > 0])
  const W = responsive && boxWidth ? Math.max(320, boxWidth) : 820
  const H = height
  const M = { top: 16, right: 16, bottom: 48, left: 64 }
  const plotW = W - M.left - M.right
  const plotH = H - M.top - M.bottom

  if (!dates?.length || !series?.length) {
    return <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>No data for this range.</Typography>
  }

  const n = dates.length
  let maxVal = 0
  for (const s of series) for (const d of dates) maxVal = Math.max(maxVal, s.points[d] ?? 0)
  const yMax = niceMax(maxVal)
  const t = (v) => (logScale ? Math.log10(1 + Math.max(0, v)) : v)
  const tMax = t(yMax) || 1

  const x = (i) => M.left + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW)
  const y = (v) => M.top + plotH - (t(v) / tMax) * plotH

  // Linear: 4 even steps. Log: 0 and each power of ten up to the top.
  const tickValues = logScale
    ? [0, ...Array.from({ length: Math.max(0, Math.ceil(Math.log10(yMax)) + 1) }, (_, k) => Math.pow(10, k)).filter((v) => v <= yMax)]
    : Array.from({ length: 5 }, (_, k) => (yMax / 4) * k)
  // About one label per 100px of plot (at most 8 at the fixed width); the
  // last date always gets one, so a step label too close to it is dropped
  // rather than printed on top of it.
  const maxLabels = responsive ? Math.max(2, Math.floor(plotW / 100)) : 8
  const xStep = Math.max(1, Math.ceil(n / maxLabels))
  const showXLabel = (i) => i === n - 1 || (i % xStep === 0 && n - 1 - i >= xStep / 2)

  return (
    <Box ref={boxRef} sx={{ position: 'relative', width: '100%', overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio={responsive ? 'xMidYMid meet' : 'none'}
        width="100%"
        height={H}
        role="img"
        aria-label="Daily cost line chart"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          const px = ((e.clientX - rect.left) / rect.width) * W
          const i = Math.max(0, Math.min(n - 1, Math.round(((px - M.left) / plotW) * (n - 1))))
          setHover({ i, x: x(i) })
        }}
      >
        {/* y gridlines + labels */}
        {tickValues.map((v) => {
          const yy = y(v)
          return (
            <g key={v}>
              <line x1={M.left} y1={yy} x2={W - M.right} y2={yy} stroke={gridColor} strokeWidth={1} />
              <text x={M.left - 8} y={yy + 4} textAnchor="end" fontSize={11} fill={labelColor}>{valueFmt(v)}</text>
            </g>
          )
        })}

        {/* x labels */}
        {dates.map((d, i) => showXLabel(i) ? (
          <text key={d} x={x(i)} y={H - M.bottom + 18} textAnchor="middle" fontSize={11} fill={labelColor}>{d.slice(5)}</text>
        ) : null)}

        {/* hover crosshair */}
        {hover && <line x1={hover.x} y1={M.top} x2={hover.x} y2={M.top + plotH} stroke={labelColor} strokeDasharray="4 3" strokeWidth={1} />}

        {/* series lines -- one <polyline> per contiguous run of present data,
            so a gap (see presentRuns) breaks the line instead of dipping to 0 */}
        {series.map((s, si) => {
          const color = s.color ?? PALETTE[si % PALETTE.length]
          const runs = presentRuns(dates, s.points)
          const hoverHasPoint = hover && s.points[dates[hover.i]] !== undefined
          return (
            <g key={s.key}>
              {runs.map((run, ri) => run.length === 1 ? (
                // A lone point surrounded by gaps on both sides -- a 1-point
                // polyline draws nothing, so mark it with a dot instead.
                <circle key={ri} cx={x(run[0])} cy={y(s.points[dates[run[0]]])} r={3} fill={color} />
              ) : (
                <polyline
                  key={ri}
                  points={run.map((i) => `${x(i)},${y(s.points[dates[i]])}`).join(' ')}
                  fill="none" stroke={color} strokeWidth={2}
                />
              ))}
              {hoverHasPoint && <circle cx={x(hover.i)} cy={y(s.points[dates[hover.i]])} r={3.5} fill={color} />}
              {showValueLabels && runs.flat().map((i) => (
                <text
                  key={`label-${i}`}
                  x={x(i)}
                  // Clamped so a value near the top of the chart doesn't get
                  // its label clipped above the viewBox.
                  y={Math.max(y(s.points[dates[i]]) - 8, M.top + 8)}
                  textAnchor="middle"
                  fontSize={10}
                  fill={color}
                >
                  {valueFmt(s.points[dates[i]])}
                </text>
              ))}
            </g>
          )
        })}
      </svg>

      {hover && (
        <Paper elevation={3} sx={{ position: 'absolute', top: 8, right: 8, p: 1, maxWidth: 280, pointerEvents: 'none' }}>
          <Typography variant="caption" fontWeight={700} display="block">{dates[hover.i]}</Typography>
          {[...series]
            .map((s, si) => ({ s, si, v: s.points[dates[hover.i]] }))
            .filter(({ v }) => v !== undefined)
            .sort((a, b) => b.v - a.v)
            .slice(0, 8)
            .map(({ s, si, v }) => (
              <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color ?? PALETTE[si % PALETTE.length], flexShrink: 0 }} />
                <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap>{s.label}</Typography>
                <Typography variant="caption" fontWeight={600} sx={{ flexShrink: 0 }}>{valueFmt(v)}</Typography>
              </Box>
            ))}
        </Paper>
      )}
    </Box>
  )
}
